import React, { useState, useEffect } from 'react';
import { 
  Newspaper, 
  ExternalLink, 
  RefreshCw,
  Clock,
  Rss,
  Globe
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useAuth, API } from '../../App';

// RSS Feed URLs for NZ Business News
const RSS_FEEDS = [
  { id: 'nzherald', name: 'NZ Herald Business', url: 'https://www.nzherald.co.nz/business/news/rss.xml' },
  { id: 'stuff', name: 'Stuff Business', url: 'https://www.stuff.co.nz/business/rss' },
  { id: 'rnz', name: 'Radio NZ Business', url: 'https://www.rnz.co.nz/rss/business.xml' },
];

const News = () => {
  const { token, user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSector, setSelectedSector] = useState('All News');
  const [sectors, setSectors] = useState(['All News']);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(response.data.news);
      setSectors(response.data.sectors || ['All News']);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
    setLoading(false);
    setRefreshing(false);
  };

  const filteredNews = selectedSector === 'All News' 
    ? news 
    : news.filter(item => item.sector === selectedSector);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl headline text-black mb-2">News</h1>
          <p className="text-black/60 body-text">
            Stay informed with the latest business news relevant to your journey.
          </p>
        </div>
        <Button
          onClick={fetchNews}
          variant="outline"
          className="rounded-none border-border"
          disabled={refreshing}
          data-testid="refresh-news-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Sector Context */}
      {user?.business_sector && (
        <div className="bg-primary/10 rounded-none p-4 border border-primary/20 flex items-center gap-3">
          <Globe className="h-5 w-5 text-primary" />
          <div>
            <p className="text-sm text-black">
              Showing news relevant to <strong>{user.business_sector}</strong>
            </p>
            <p className="text-xs text-black/60">
              Update your sector in your profile to personalise this feed.
            </p>
          </div>
        </div>
      )}

      {/* Sector Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sectors.map((sector) => (
          <button
            key={sector}
            onClick={() => setSelectedSector(sector)}
            className={`px-4 py-2 rounded-none text-sm transition-all whitespace-nowrap ${
              selectedSector === sector
                ? 'bg-primary text-white subhead'
                : 'bg-secondary text-black/60 hover:bg-secondary/80'
            }`}
          >
            {sector}
          </button>
        ))}
      </div>

      {/* News Feed */}
      {loading ? (
        <div className="text-center py-12 text-black/40">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3" />
          <p>Loading news...</p>
        </div>
      ) : filteredNews.length > 0 ? (
        <div className="space-y-4">
          {filteredNews.map((item, idx) => (
            <NewsCard key={item.id || idx} item={item} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-none p-8 border border-border text-center">
          <Newspaper className="h-12 w-12 text-black/20 mx-auto mb-3" />
          <p className="text-black/40">No news articles found.</p>
          <p className="text-sm text-black/30 mt-1">
            Check back later for updates.
          </p>
        </div>
      )}

      {/* RSS Sources */}
      <div className="bg-white rounded-none p-6 border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Rss className="h-5 w-5 text-black/40" />
          <h3 className="subhead text-black">News Sources</h3>
        </div>
        <p className="text-sm text-black/60 mb-4">
          News is curated from leading New Zealand business publications.
        </p>
        <div className="flex flex-wrap gap-3">
          {RSS_FEEDS.map((feed) => (
            <span
              key={feed.id}
              className="text-xs px-3 py-1.5 bg-secondary rounded-none text-black/60"
            >
              {feed.name}
            </span>
          ))}
        </div>
      </div>

      {/* Brian Tip */}
      <div className="bg-secondary/50 rounded-none p-6">
        <h3 className="subhead text-black mb-2">Ask Brian about the news</h3>
        <p className="text-sm text-black/60">
          "Brian, what does this mean for my business idea?" — Brian can help you understand 
          how current events might affect your entrepreneurial journey.
        </p>
      </div>
    </div>
  );
};

const NewsCard = ({ item }) => {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-none p-5 border border-border hover:border-primary/30 transition-all group"
      data-testid={`news-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-black/40 mb-2">
            <span className="font-medium">{item.source}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {item.time}
            </span>
            {item.sector && item.sector !== 'All News' && (
              <>
                <span>•</span>
                <span className="bg-secondary px-2 py-0.5 rounded-none">{item.sector}</span>
              </>
            )}
          </div>
          <h3 className="subhead text-black group-hover:text-primary transition-colors line-clamp-2 mb-1">
            {item.headline}
          </h3>
          {item.summary && (
            <p className="text-sm text-black/50 line-clamp-2">{item.summary}</p>
          )}
        </div>
        <ExternalLink className="h-4 w-4 text-black/30 group-hover:text-primary flex-shrink-0 mt-1" />
      </div>
    </a>
  );
};

export default News;
